const BREADCRUMB_STORAGE_KEY_PREFIX = 'rosfo:breadcrumb-trail';

function getBreadcrumbStorageKey() {
  const runtimeData = document.querySelector('[data-breadcrumb-runtime]');

  const locale =
    runtimeData?.dataset.locale ||
    document.documentElement.lang ||
    'default';

  const country =
    runtimeData?.dataset.country ||
    'default';

  return `${BREADCRUMB_STORAGE_KEY_PREFIX}:${locale}:${country}`;
}
const IGNORED_QUERY_PARAMETERS = new Set([
  'preview_theme_id',
  'view',
  'variant',
  'option_values',
  '_pos',
  '_sid',
  '_ss',
  'pr_prod_strat',
  'pr_rec_id',
  'pr_rec_pid',
  'pr_ref_pid',
  'pr_seq',
]);

function normalizePath(url) {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    if (parsedUrl.origin !== window.location.origin) return null;

    const pathname = parsedUrl.pathname.replace(/\/+$/, '');
    for (const parameter of IGNORED_QUERY_PARAMETERS) parsedUrl.searchParams.delete(parameter);
    parsedUrl.searchParams.sort();

    const search = parsedUrl.searchParams.toString();
    return `${pathname || '/'}${search ? `?${search}` : ''}`;
  } catch (_error) {
    return null;
  }
}

function currentUrl() {
  return `${window.location.pathname}${window.location.search}`;
}

function getInitialTrail(breadcrumb) {
  return Array.from(breadcrumb.querySelectorAll('.rosfo-breadcrumbs__item'))
    .map((item) => {
      const link = item.querySelector('a');
      const title = item.textContent?.trim();
      const url = link?.getAttribute('href') || currentUrl();

      return title && normalizePath(url) ? { title, url } : null;
    })
    .filter(Boolean);
}

function getLevelOneLinks(runtimeData) {
  if (!runtimeData) return [];

  return Array.from(runtimeData.querySelectorAll('[data-breadcrumb-level-one-link]'))
    .map((link) => {
      const title = link.dataset.title?.trim();
      const url = link.dataset.url;

      return title && normalizePath(url) ? { title, url } : null;
    })
    .filter(Boolean);
}

function findLevelOne(trail, levelOneLinks) {
  return trail.find((item) => {
    const itemPath = normalizePath(item.url);
    return levelOneLinks.some((link) => normalizePath(link.url) === itemPath);
  });
}

function isValidRoot(item, rootUrl, levelOneLinks) {
  const itemPath = normalizePath(item?.url);
  if (!itemPath) return false;

  return itemPath === normalizePath(rootUrl) || levelOneLinks.some((link) => normalizePath(link.url) === itemPath);
}

function getStructuralTrail(initialTrail, levelOneLinks) {
  const levelOneRoot = findLevelOne(initialTrail, levelOneLinks);
  if (!levelOneRoot) return initialTrail;

  const rootIndex = initialTrail.findIndex((item) => normalizePath(item.url) === normalizePath(levelOneRoot.url));
  return initialTrail.slice(rootIndex);
}

function readTrail() {
  const storedTrail = JSON.parse(
    sessionStorage.getItem(getBreadcrumbStorageKey()) || '[]'
  );

  if (!Array.isArray(storedTrail)) return [];

  return storedTrail.filter(
    (item) =>
      typeof item?.title === 'string' &&
      item.title.trim() &&
      typeof item?.url === 'string' &&
      normalizePath(item.url)
  );
}

function writeTrail(trail) {
  sessionStorage.setItem(
    getBreadcrumbStorageKey(),
    JSON.stringify(trail)
  );
}

function renderTrail(breadcrumb, trail) {
  const list = breadcrumb.querySelector('.rosfo-breadcrumbs__list');
  const scrollContainer = breadcrumb.querySelector('.rosfo-breadcrumbs__inner');
  if (!list || !scrollContainer || trail.length === 0) return;

  list.replaceChildren();

  trail.forEach((item, index) => {
    if (index > 0) {
      const separator = document.createElement('li');
      separator.className = 'rosfo-breadcrumbs__separator';
      separator.setAttribute('aria-hidden', 'true');
      separator.textContent = '›';
      list.append(separator);
    }

    const listItem = document.createElement('li');
    listItem.className = 'rosfo-breadcrumbs__item';

    if (index === trail.length - 1) {
      listItem.classList.add('rosfo-breadcrumbs__item--current');
      const currentItem = document.createElement('span');
      currentItem.setAttribute('aria-current', 'page');
      currentItem.textContent = item.title;
      listItem.append(currentItem);
    } else {
      const link = document.createElement('a');
      link.href = item.url;
      link.textContent = item.title;
      link.addEventListener('click', () => {
        try {
          writeTrail(trail.slice(0, index + 1));
        } catch (_error) {
          // The rendered Liquid breadcrumb remains available when sessionStorage is blocked.
        }
      });
      listItem.append(link);
    }

    list.append(listItem);
  });

  requestAnimationFrame(() => {
    scrollContainer.scrollLeft = scrollContainer.scrollWidth;
  });
}

function syncBreadcrumbs() {
  const runtimeData = document.querySelector('[data-breadcrumb-runtime]');
  const rootUrl = runtimeData?.dataset.rootUrl || window.Shopify?.routes?.root || '/';
  const currentPath = normalizePath(window.location.href);
  const breadcrumb = document.querySelector('.rosfo-breadcrumbs');

  try {
    if (currentPath === normalizePath(rootUrl)) {
      writeTrail([{ title: runtimeData.dataset.rootTitle, url: rootUrl }]);
      return;
    }

    const levelOneLinks = getLevelOneLinks(runtimeData);
    const currentLevelOne = levelOneLinks.find((link) => normalizePath(link.url) === currentPath);

    if (currentLevelOne) {
      writeTrail([currentLevelOne]);
      return;
    }

    if (!breadcrumb) return;

    const initialTrail = getInitialTrail(breadcrumb);
    if (initialTrail.length === 0) return;

    const structuralTrail = getStructuralTrail(initialTrail, levelOneLinks);
    let trail = readTrail();

    const referrerUrl = document.referrer
      ? new URL(document.referrer)
      : null;

    const referrerOrigin = referrerUrl?.origin ?? null;

    const navigationEntry =
      performance.getEntriesByType?.('navigation')?.[0];

    const navigationType = navigationEntry?.type;

    const enteredFromExternalSite =
      referrerOrigin !== null &&
      referrerOrigin !== window.location.origin;

    const enteredDirectly =
      navigationType === 'navigate' &&
      referrerUrl === null;

    const hasValidRoot = isValidRoot(
      trail[0],
      rootUrl,
      levelOneLinks
    );

    if (
      trail.length === 0 ||
      enteredFromExternalSite ||
      enteredDirectly ||
      !hasValidRoot
    ) {
      trail = structuralTrail;
    } else {
      const existingIndex = trail.findIndex((item) => normalizePath(item.url) === currentPath);

      if (existingIndex >= 0) {
        trail = trail.slice(0, existingIndex + 1);
        trail[existingIndex] = initialTrail.at(-1);
      } else {
        trail.push(initialTrail.at(-1));
      }
    }

    writeTrail(trail);
    renderTrail(breadcrumb, trail);
  } catch (_error) {
    // Keep the server-rendered Shopify breadcrumb when sessionStorage is unavailable.
  } finally {
    breadcrumb?.removeAttribute('data-breadcrumb-pending');
  }
}

syncBreadcrumbs();
window.addEventListener('pageshow', syncBreadcrumbs);
window.addEventListener('pagehide', () => {
  document.querySelector('.rosfo-breadcrumbs')?.setAttribute('data-breadcrumb-pending', '');
});
